import type React from 'react';
import { useCallback } from 'react';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { Waypoint } from '#src/app/shared/components/scroll';
import { useTrackOnce } from '#src/app/shared/hooks/tracking';

import { claimCarouselImpression } from './carouselImpressionRegistry';

type Props = Readonly<{
	/** Distinct signers rendered, before the feed doubles them for the seamless scroll effect. */
	signerCount: number;
}>;

/**
 * Fires `sign_form_recent_signers_carousel_impression` once per petition, when the recently signed
 * carousel actually reaches the viewport.
 *
 * This is what separates "the carousel rendered" from "the sign form was opened", which is the population
 * the last-name-initial experiment needs to be read against — no such event existed before. It carries no
 * experiment dependency on purpose, so it can ship ahead of the experiment and establish baseline volume.
 *
 * Because it has no experiment dependency it also fires for sessions that were never bucketed (fallback,
 * QA override, or the experiment not running at all). **Intersect it with `$exposure` before using it as an
 * analysis denominator** — on its own it is a surface-reach metric, not an experiment one.
 *
 * `onEnter`, not `onPositionChange`: the latter fires on every delivered entry, and a `display: none`
 * copy of the carousel has an all-zero rect that `calculatePosition` classifies as `'inside'` — which
 * would report impressions for a carousel nobody can see.
 *
 * Mount this outside the feed's `gap-3` flex scroller. `Waypoint` renders a real in-flow `<span>`, which
 * as a direct child of a gapped flex container silently consumes a whole gap unit.
 */
export const RecentSignersFeedImpression: React.FC<Props> = ({ signerCount }) => {
	const {
		data: {
			petition: { id },
		},
	} = usePetitionDetailsPageData();

	const petitionId = `petition_${id}`;
	const trackOnce = useTrackOnce([petitionId]);

	const onEnter = useCallback(() => {
		if (!claimCarouselImpression(petitionId)) return;

		void trackOnce('sign_form_recent_signers_carousel_impression', {
			petition_id: petitionId,
			recent_signers_count: signerCount,
		});
	}, [petitionId, signerCount, trackOnce]);

	return <Waypoint onEnter={onEnter} />;
};
